import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { toast } from '../../lib/toast';
import { normalizeWhatsAppNumber, buildWaMeLink } from '../../lib/whatsapp';
import Icon from '../../components/dashboard/DashboardIcons';
import { API, auth, errMsg, PrimaryBtn, Field, Modal, inputCls } from '../organization/finance/shared';
import { statusMeta } from '../organization/venue-booking/bookingStatusMeta';
import { PaymentMethodBadge } from '../organization/venue-booking/PaymentMethodBadge';
import PaymentProofModal from '../organization/venue-booking/PaymentProofModal';

const CANCELLABLE = ['booked', 'pending', 'pending_payment', 'pending_payment_verification'];
const BLANK_FORM = { venue_id: '', court_id: '', date: '', start_time: '', end_time: '', status: 'booked', customer_name: '', customer_phone: '', notes: '' };

export default function VenueOwnerBookingsPage() {
  const { venues } = useOutletContext();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', venue_id: '', date_from: '', date_to: '', search: '' });
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK_FORM);
  const [courts, setCourts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [proofBooking, setProofBooking] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const res = await axios.get(`${API}/venues/bookings/search`, { ...auth(), params });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch { setItems([]); } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const loadCourts = async (venueId) => {
    if (!venueId) { setCourts([]); return; }
    try {
      const res = await axios.get(`${API}/venues/${venueId}/courts`, auth());
      setCourts(res.data || []);
    } catch { setCourts([]); }
  };

  const openManual = () => { setEditing(null); setForm(BLANK_FORM); setCourts([]); setShowModal(true); };
  const openEdit = (b) => {
    setEditing(b);
    setForm({ venue_id: b.venue_id, court_id: b.court_id || '', date: b.date, start_time: b.start_time, end_time: b.end_time, status: b.status, customer_name: b.customer_name || '', customer_phone: b.customer_phone || '', notes: b.notes || '' });
    loadCourts(b.venue_id);
    setShowModal(true);
  };

  const save = async () => {
    if (!form.venue_id || !form.date || !form.start_time || !form.end_time) { toast.error('اختر الملعب والتاريخ والوقت'); return; }
    setSaving(true);
    const payload = { date: form.date, start_time: form.start_time, end_time: form.end_time, status: form.status, court_id: form.court_id || null, customer_name: form.customer_name || null, customer_phone: form.customer_phone || null, notes: form.notes || null };
    try {
      if (editing) await axios.put(`${API}/venues/${form.venue_id}/bookings/${editing.id}`, payload, auth());
      else await axios.post(`${API}/venues/${form.venue_id}/bookings`, payload, auth());
      toast.success('تم الحفظ بنجاح');
      setShowModal(false);
      load();
    } catch (e) { toast.error(errMsg(e, 'فشل حفظ الحجز')); } finally { setSaving(false); }
  };

  const act = async (b, action, msg) => {
    try {
      await axios.post(`${API}/venues/${b.venue_id}/bookings/${b.id}/${action}`, {}, auth());
      toast.success(msg);
      load();
    } catch (e) { toast.error(errMsg(e, 'فشل تنفيذ الإجراء')); }
  };

  const remove = async (b) => {
    try { await axios.delete(`${API}/venues/${b.venue_id}/bookings/${b.id}`, auth()); toast.success('تم حذف الحجز'); load(); }
    catch (e) { toast.error(errMsg(e, 'فشل الحذف')); }
  };

  const waLink = (b) => {
    const num = normalizeWhatsAppNumber(b.customer_phone);
    if (!num) return null;
    return buildWaMeLink(num, `مرحباً ${b.customer_name || ''}، بخصوص حجزك في ${b.venue_name} يوم ${b.date} من ${b.start_time} إلى ${b.end_time}.`);
  };

  return (
    <div className="space-y-4" data-testid="venue-owner-bookings-page">
      <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-slate-200 bg-white p-3">
        <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className={`${inputCls} w-auto`} data-testid="venue-owner-bookings-filter-status">
          <option value="">كل الحالات</option>
          <option value="pending">قيد المراجعة</option>
          <option value="pending_payment">بانتظار الدفع</option>
          <option value="pending_payment_verification">بانتظار التحقق من الدفع</option>
          <option value="booked">مؤكد</option>
          <option value="rejected">مرفوض</option>
          <option value="cancelled">ملغي</option>
          <option value="blocked">محظور</option>
        </select>
        <select value={filters.venue_id} onChange={(e) => setFilters({ ...filters, venue_id: e.target.value })} className={`${inputCls} w-auto`} data-testid="venue-owner-bookings-filter-venue">
          <option value="">كل الملاعب</option>
          {venues.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
        <input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className={`${inputCls} w-auto`} data-testid="venue-owner-bookings-filter-date-from" />
        <input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className={`${inputCls} w-auto`} data-testid="venue-owner-bookings-filter-date-to" />
        <input placeholder="بحث بالاسم أو الجوال" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} className={`${inputCls} w-auto flex-1 min-w-[160px]`} data-testid="venue-owner-bookings-filter-search" />
        <PrimaryBtn onClick={openManual} data-testid="venue-owner-bookings-manual-add-btn"><Icon name="plus" className="h-4 w-4" /> حجز يدوي / حظر وقت</PrimaryBtn>
      </div>

      {loading ? (
        <div className="h-40 animate-pulse rounded-2xl bg-slate-100" />
      ) : items.length === 0 ? (
        <p className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-400" data-testid="venue-owner-bookings-empty">لا توجد حجوزات مطابقة</p>
      ) : (
        <div className="space-y-2" data-testid="venue-owner-bookings-list">
          <p className="text-xs text-slate-400">{total} نتيجة</p>
          {items.map((b) => {
            const meta = statusMeta(b.status);
            return (
            <div key={b.id} className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-slate-200 bg-white p-3.5" data-testid={`venue-owner-booking-row-${b.id}`}>
              <div className="min-w-[180px]">
                <p className="text-sm font-bold text-slate-900">{b.venue_name} {b.court_label ? `— ${b.court_label}` : ''}</p>
                {b.booking_number && <span dir="ltr" data-testid={`venue-owner-booking-number-${b.id}`} className="mt-0.5 inline-block rounded-full bg-slate-800 px-2 py-0.5 text-[11px] font-mono font-bold text-white">{b.booking_number}</span>}
                <p className="text-xs text-slate-500">{b.date} • {b.start_time}-{b.end_time}</p>
                <p className="text-xs text-slate-500">{b.customer_name || 'بلا اسم'} • {b.customer_phone || '—'}</p>
                {b.payment_method && <div className="mt-1"><PaymentMethodBadge methodId={b.payment_method} testId={`venue-owner-booking-payment-method-${b.id}`} /></div>}
              </div>
              <span className={`rounded-full border px-2.5 py-1 text-xs font-bold ${meta.badge}`}>{meta.label}</span>
              <div className="flex flex-wrap gap-1.5">
                {b.status === 'pending' && (
                  <>
                    <button onClick={() => act(b, 'approve', 'تم قبول الحجز')} className="rounded-lg border border-emerald-200 px-2.5 py-1.5 text-xs font-bold text-emerald-700" data-testid={`venue-owner-booking-approve-${b.id}`}>قبول</button>
                    <button onClick={() => act(b, 'reject', 'تم رفض الحجز')} className="rounded-lg border border-red-200 px-2.5 py-1.5 text-xs font-bold text-red-600" data-testid={`venue-owner-booking-reject-${b.id}`}>رفض</button>
                  </>
                )}
                {(b.status === 'pending_payment' || b.status === 'pending_payment_verification') && (
                  <>
                    {b.status === 'pending_payment_verification' && (
                      <>
                        <button onClick={() => setProofBooking(b)} className="rounded-lg border border-blue-200 px-2.5 py-1.5 text-xs font-bold text-blue-700" data-testid={`venue-owner-booking-view-proof-${b.id}`}>عرض الإثبات</button>
                        <button onClick={() => act(b, 'reject-proof', 'تم رفض الإثبات')} className="rounded-lg border border-red-200 px-2.5 py-1.5 text-xs font-bold text-red-600" data-testid={`venue-owner-booking-reject-proof-${b.id}`}>رفض الإثبات</button>
                      </>
                    )}
                    <button onClick={() => act(b, 'confirm-payment', 'تم تأكيد استلام الدفع')} className="rounded-lg border border-emerald-200 px-2.5 py-1.5 text-xs font-bold text-emerald-700" data-testid={`venue-owner-booking-confirm-payment-${b.id}`}>تأكيد استلام الدفع</button>
                  </>
                )}
                {waLink(b) && <a href={waLink(b)} target="_blank" rel="noreferrer" className="rounded-lg border border-emerald-200 px-2.5 py-1.5 text-xs font-bold text-emerald-700" data-testid={`venue-owner-booking-whatsapp-${b.id}`}>واتساب</a>}
                <button onClick={() => openEdit(b)} className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-bold text-slate-600" data-testid={`venue-owner-booking-edit-${b.id}`}>تعديل</button>
                {CANCELLABLE.includes(b.status) && (
                  <button onClick={() => act(b, 'cancel', 'تم إلغاء الحجز')} className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-bold text-slate-600" data-testid={`venue-owner-booking-cancel-${b.id}`}>إلغاء</button>
                )}
                <button onClick={() => remove(b)} className="rounded-lg border border-red-200 px-2.5 py-1.5 text-xs font-bold text-red-600" data-testid={`venue-owner-booking-delete-${b.id}`}>حذف</button>
              </div>
            </div>
            );
          })}
        </div>
      )}

      <Modal open={showModal} title={editing ? 'تعديل الحجز' : 'حجز يدوي / حظر وقت'} onClose={() => setShowModal(false)} wide>
        <div className="grid grid-cols-2 gap-3">
          <Field label="الملعب" required><select className={inputCls} value={form.venue_id} onChange={(e) => { setForm({ ...form, venue_id: e.target.value, court_id: '' }); loadCourts(e.target.value); }} data-testid="venue-owner-booking-form-venue">
            <option value="">اختر الملعب</option>
            {venues.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select></Field>
          <Field label="المقاس (اختياري)"><select className={inputCls} value={form.court_id} onChange={(e) => setForm({ ...form, court_id: e.target.value })} data-testid="venue-owner-booking-form-court">
            <option value="">بلا مقاس محدد</option>
            {courts.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select></Field>
          <Field label="التاريخ" required><input type="date" className={inputCls} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="venue-owner-booking-form-date" /></Field>
          <Field label="الحالة"><select className={inputCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="venue-owner-booking-form-status">
            <option value="booked">مؤكد</option>
            <option value="pending">قيد المراجعة</option>
            <option value="blocked">حظر وقت</option>
          </select></Field>
          <Field label="من الساعة" required><input type="time" className={inputCls} value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="venue-owner-booking-form-start" /></Field>
          <Field label="إلى الساعة" required><input type="time" className={inputCls} value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="venue-owner-booking-form-end" /></Field>
          <Field label="اسم العميل"><input className={inputCls} value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="venue-owner-booking-form-customer-name" /></Field>
          <Field label="جوال العميل"><input dir="ltr" className={inputCls} value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} data-testid="venue-owner-booking-form-customer-phone" /></Field>
          <div className="col-span-2"><Field label="ملاحظة"><input className={inputCls} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="venue-owner-booking-form-notes" /></Field></div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => setShowModal(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600">إلغاء</button>
          <PrimaryBtn onClick={save} disabled={saving} data-testid="venue-owner-booking-form-submit">{saving ? 'جارٍ الحفظ...' : 'حفظ'}</PrimaryBtn>
        </div>
      </Modal>

      {proofBooking && <PaymentProofModal venueId={proofBooking.venue_id} booking={proofBooking} onClose={() => setProofBooking(null)} />}
    </div>
  );
}
